import { Injectable } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { compareCustomFieldColumns } from '../../custom-fields/combine-custom-fields/compare-custom-fields';
import { GET_CUSTOM_FIELDS } from '../../custom-fields/custom-fields.store';
import { TWorkspaceLayout } from '../../preferences/preferences.model';
import { EWorkspaces } from '../../workspace/workspaces.enum';
import { SiteOptionsService } from '../site-options/site-options.service';
import { TColumn } from '../site-table/columns/column.model';
import { generateDefaultOverviewColumns } from '../site-table/columns/columns';
import { CustomTableService } from '../site-table/custom-table/custom-table.service';

@Injectable({
  providedIn: 'root',
})
export class SiteTableOverviewService {
  constructor(
    private siteOptionsService: SiteOptionsService,
    private customTableService: CustomTableService,
  ) {}

  processOverviewLayout(layout: TWorkspaceLayout, columns: TColumn[]): void {
    const table = this.customTableService.getTable();
    const sortButton = this.siteOptionsService.getSortButton();
    const groupButton = this.siteOptionsService.getGroupButton();

    if (layout?.columns) {
      layout.columns = this.filterOverviewColumns(layout.columns, columns);
      layout.columns = this.addMissingColumns(layout.columns);
      this.findExistingColumns(columns, layout);

      layout.columns = this.fixColumnIndexes(layout.columns);
      table.updateColumns({ columns: cloneDeep(layout.columns) });
    } else {
      table.updateColumns();
    }

    table.setKeyword('');
    table.updateWidth();
    table.load({ keepScrollPosition: false });
    table.reload({
      reset: true,
      isOverview: true,
    });

    if (sortButton) {
      sortButton.update();
    }

    if (groupButton) {
      groupButton.update();
    }
  }

  filterOverviewColumns(preferencesColumns: TColumn[], columns: TColumn[]): TColumn[] {
    let newColumns = preferencesColumns.filter((column) => {
      if (column.customFieldIds) {
        const existingColumn = columns.find((_column) => {
          let match = false;

          if (_column?.customFieldIds) {
            _column?.customFieldIds.forEach((fieldId) => {
              if (column.customFieldIds.includes(fieldId)) {
                match = true;
              }
            });

            return match;
          } else {
            return column.name === _column.name;
          }
        });

        if (existingColumn) {
          column.currency = existingColumn.currency;
          column.decimalPlaces = existingColumn.decimalPlaces;
          column.unit = existingColumn.unit;
          column.subValuesActive = existingColumn.subValuesActive;

          if (existingColumn.showTotal || typeof existingColumn.showTotal === 'undefined') {
            column.showTotal = true;
          }

          if (
            !existingColumn.subValuesActive ||
            typeof existingColumn.subValuesActive === 'undefined'
          ) {
            column.subValuesActive = false;
          }

          return true;
        }

        return false;
      }

      return true;
    });

    newColumns = preferencesColumns.filter((column) => {
      if (!column.customFieldIds) {
        return true;
      } else {
        const customFields = GET_CUSTOM_FIELDS();

        column.customFieldIds = column.customFieldIds.filter((customFieldId) => {
          const customField = customFields[customFieldId];
          const subfieldsMatch = customField?.subValuesActive === column.subValuesActive;

          return !!customField && subfieldsMatch;
        });

        const existingColumns = preferencesColumns.filter((_column) =>
          compareCustomFieldColumns(_column, column, EWorkspaces.OVERVIEW),
        );

        if (existingColumns.length > 1) {
          if (!isEqual(existingColumns[0].customFieldIds, column.customFieldIds)) {
            existingColumns[0].customFieldIds = [
              ...existingColumns[0].customFieldIds,
              ...column.customFieldIds,
            ];

            return false;
          }
        }

        if (column.customFieldIds.length === 0) {
          return false;
        }

        return true;
      }
    });

    return newColumns;
  }

  private findExistingColumns(columns: TColumn[], layout: TWorkspaceLayout): void {
    columns.forEach((column) => {
      if (column.customFieldIds) {
        const existingColumn = layout.columns.find((_column) =>
          compareCustomFieldColumns(_column, column, EWorkspaces.OVERVIEW),
        );

        if (!existingColumn) {
          const columnToMerge = layout.columns.find((_column) =>
            compareCustomFieldColumns(_column, column, EWorkspaces.OVERVIEW),
          );

          if (columnToMerge) {
            column.customFieldIds.forEach((fieldId) => {
              if (!columnToMerge.customFieldIds.includes(fieldId)) {
                columnToMerge.customFieldIds.push(fieldId);
              }
            });
          } else {
            layout.columns.push(column);
          }
        } else {
          column.customFieldIds.forEach((fieldId) => {
            if (
              existingColumn?.customFieldIds &&
              !existingColumn.customFieldIds.includes(fieldId)
            ) {
              existingColumn.customFieldIds.push(fieldId);
            }
          });
        }
      }
    });
  }

  private fixColumnIndexes(columns: TColumn[]): TColumn[] {
    return columns.map((column, index) => ({ ...column, index }));
  }

  private addMissingColumns(layoutColumns: TColumn[]): TColumn[] {
    const defaultColumns = generateDefaultOverviewColumns();

    defaultColumns.forEach((defaultColumn, index) => {
      const existingColumn = layoutColumns.find((column) => column.name === defaultColumn.name);

      if (!existingColumn) {
        layoutColumns.splice(index, 0, defaultColumn);
      }
    });

    return layoutColumns;
  }
}
